import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button, Link, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import { DocumentCopyIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { LoanClosingSearchParams } from 'app/pages/loanApplication/tabs/loanClosingCDS/LoanClosingCDS.constants';
import AmortizationSchedule from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/AmortizationSchedule';
import {
  openCurrentPageInNewTab,
  removeAmortizationSearchParam,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/AmortizationSchedule/AmortizationScheduleTable.utils';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import useUrlSearchParams from 'hooks/useUrlSearchParams';

import { openDialogAction } from 'store/global/actions';
import {
  clearClosingAmortizationScheduleAction,
  getClosingAmortizationScheduleAction,
} from 'store/loanApplications/actions';

import { getClosingDocumentsConfig } from '../../LoanClosingCDS.config';
import {
  allDocumentsListStyles,
  documentLinkStyles,
  documentsColumnStyles,
  documentsGridStyles,
  documentsLabelStyles,
} from '../../LoanClosingCDS.styles';
import FieldRow from '../FieldRow';

import { AMORTIZATION_SCHEDULE_OPENED } from './ClosingDocumentsSection.constants';
import { amortizationButtonWrapperStyles, modalHeadStyles } from './ClosingDocumentsSection.styles';
import { ClosingDocumentsSectionTestIds } from './ClosingDocumentsSection.testIds';

const ClosingDocumentsSection = ({ closingDocuments = [], isModalOpenRef }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { applicationId } = useParams();
  const searchParams = useUrlSearchParams();
  const docConfig = useMemo(() => getClosingDocumentsConfig(closingDocuments), [closingDocuments]);

  const isSchedulePreopened =
    searchParams[LoanClosingSearchParams.AMORTIZATION_SCHEDULE] === AMORTIZATION_SCHEDULE_OPENED;

  const isOpenedFromUrlRef = useRef(isSchedulePreopened);

  const removeAmortizationParam = useCallback(() => {
    removeAmortizationSearchParam(history, isModalOpenRef);
  }, [history, isModalOpenRef]);

  const onOpenAmortizationSchedule = useCallback(
    e => {
      e?.stopPropagation();

      const url = new URL(window.location.href);

      url.searchParams.set(
        LoanClosingSearchParams.AMORTIZATION_SCHEDULE,
        AMORTIZATION_SCHEDULE_OPENED,
      );
      history.replace(`${url.pathname}${url.search}`);
    },
    [history],
  );

  const openAmortizationModal = useCallback(() => {
    if (isModalOpenRef.current) {
      return;
    }

    isModalOpenRef.current = true;

    const isFullScreen = isOpenedFromUrlRef.current;

    dispatch(clearClosingAmortizationScheduleAction());
    dispatch(getClosingAmortizationScheduleAction({ applicationId }));

    dispatch(
      openDialogAction({
        customHead: (
          <Box sx={modalHeadStyles}>
            <Typography variant="medium16">Amortization Schedule</Typography>

            {!isFullScreen && (
              <Button
                data-testid={ClosingDocumentsSectionTestIds.OPEN_NEW_TAB_BUTTON}
                size="small"
                variant="contained"
                onClick={openCurrentPageInNewTab}
              >
                Open in New Tab
              </Button>
            )}
          </Box>
        ),
        size: ModalSize.FULL,
        fullScreen: isFullScreen,
        afterCloseCallback: removeAmortizationParam,
        body: ({ formRef, dialogId }) => (
          <AmortizationSchedule
            dialogId={dialogId}
            formRef={formRef}
            isSchedulePreopened={isFullScreen}
          />
        ),
      }),
    );
  }, [applicationId, dispatch, isModalOpenRef, removeAmortizationParam]);

  useEffect(() => {
    if (!isSchedulePreopened) {
      return;
    }

    openAmortizationModal();
  }, [isSchedulePreopened, openAmortizationModal]);

  return (
    <PageBlock title="Closing Documents">
      <Box sx={documentsGridStyles}>
        <Box sx={documentsColumnStyles}>
          <FieldRow bordered={false} label="Last Generated:" value={docConfig.lastGenerated} />
        </Box>
        <Box sx={documentsColumnStyles}>
          <Typography color="text.secondary" sx={documentsLabelStyles} variant="regular14">
            Documents:
          </Typography>
          {closingDocuments?.length ? (
            <Box sx={allDocumentsListStyles}>
              {closingDocuments.map(({ name, url }, index) => (
                <Link
                  data-testid={ClosingDocumentsSectionTestIds.getDocumentLinkId(index)}
                  href={url}
                  key={`${name}-${url}`}
                  rel="noopener noreferrer"
                  sx={documentLinkStyles}
                  target="_blank"
                >
                  <DocumentCopyIcon size={IconSize[16]} />
                  <Typography variant="medium14">{name}</Typography>
                </Link>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" variant="regular14">
              -
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={amortizationButtonWrapperStyles}>
        <Button
          data-testid={ClosingDocumentsSectionTestIds.AMORTIZATION_BUTTON}
          size="small"
          variant="line"
          onClick={onOpenAmortizationSchedule}
        >
          Amortization Schedule
        </Button>
      </Box>
    </PageBlock>
  );
};

ClosingDocumentsSection.propTypes = {
  isModalOpenRef: PropTypes.shape({ current: PropTypes.bool }).isRequired,
  closingDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
};

export default ClosingDocumentsSection;
